import {
  QueryClient,
  useIsMutating,
  useMutation,
  useQueryClient,
} from "@tanstack/react-query";
import { useLocalSearchParams, useRouter } from "expo-router";
import { useCallback, useContext } from "react";
import { Platform } from "react-native";

import api from "@/api";
import {
  getGetUserProfileQueryKey,
  useCreateConversation,
  useMarkConversationAsRead as useGeneratedMarkConversationAsRead,
  useSaveNotificationToConversation as useGeneratedSaveNotificationToConversation,
  useUpdateEvent as useGeneratedUpdateEvent,
  useSendMessageInCuraiVisit,
} from "@/api/generated";
import {
  ConversationModel,
  SendCuraiMessageRequest,
  TextSendMessageRequestType,
  WorkflowType,
} from "@/api/generated/models";
import {
  invalidateConversationHistory,
  setConversationGroupRead,
  setConversationRead,
} from "@/api/queries/conversation";
import { LayoutContext } from "@/components/LayoutProvider";
import { AD_CONVERSION_EVENTS } from "@/config/adConversionEvents";
import { parseEventStream } from "@/conversation/serverSentEvents";
import { useAnalytics, useEventContext } from "@/hooks/analytics";
import {
  useCurrentConversationId,
  useJoinGroupOnSend,
} from "@/hooks/conversation";
import { UseCase } from "@/hooks/suggestions";
import {
  createUserSentMessage,
  infiniteMessagesQueryKey,
  updateInfiniteMessagesFromQuestionAnswers,
  updateInfiniteMessagesFromUser,
  useInfiniteMessages,
} from "@/hooks/useInfiniteMessages";
import { usePartnerIntent } from "@/hooks/usePartnerIntent";
import { usePropagatedRouter } from "@/hooks/usePropagatedRouter";
import { logger, setTag } from "@/sentry";
import { useNudgeStore, useUseCaseStore, useUserSentEventStore } from "@/store";
import {
  type AskUserQuestionAnswer,
  getStructuredPartialAnswersAndClear,
} from "@/store/askUserQuestions";
import {
  ConversationComposition,
  useConversationComposition,
} from "@/store/conversation";
import { Agent } from "@/types/agent";

type SendMessageRequestType =
  | TextSendMessageRequestType
  | "rx_renewal_selected_medication"
  | "ask_user_questions_response";
const SendMessageRequestType = {
  ...TextSendMessageRequestType,
  rx_renewal_selected_medication: "rx_renewal_selected_medication" as const,
  ask_user_questions_response: "ask_user_questions_response" as const,
};

type HeightWeight = {
  height_in: number;
  weight_lb: number;
};

export type AttachmentType = {
  localUri: string;
  attachmentId?: string;
  presignedUrl?: string;
  fileSize?: number;
  name?: string | null;
  mimeType?: string | null;
  isImage: boolean;
  error?: string;
};

export const getAudioExtensionFromMimeType = (type: string): string => {
  const mimeType = type?.split(";")[0] ?? "";
  const extension = mimeType.split("/")[1];
  return extension || "m4a"; // last ditch, assume m4a
};

const postMessageStream = async (
  queryClient: QueryClient,
  setIsThinking: ConversationComposition["setIsThinking"],
  setInputControl: ConversationComposition["setInputControl"],
  conversationId: string,
  userSentMessage: string | object[],
  agent?: Agent,
  useCase?: UseCase,
  utmTerm?: string,
  attachmentIds?: string[],
  type?: SendMessageRequestType,
  heightWeight?: HeightWeight,
) => {
  logger.info("Posted to message stream", {
    category: "api",
    text: userSentMessage,
  });
  const response = await api.post(
    `/chat/${conversationId}`,
    {
      message: {
        type: type ?? "user_input",
        content: userSentMessage,
        height_in: heightWeight?.height_in,
        weight_lb: heightWeight?.weight_lb,
      },
      initial_agent: agent,
      contexts: { theme: useCase, utm_term: utmTerm },
      attachment_ids: attachmentIds,
    },
    {
      responseType: "stream",
      adapter: "fetch",
    },
  );
  const reader = response.data.getReader();
  await parseEventStream(
    reader,
    queryClient,
    setIsThinking,
    setInputControl,
    conversationId,
  );
};

const postVoiceMessageTranscription = async (
  audioUri: string,
  conversationId?: string,
) => {
  const formData = new FormData();
  if (conversationId) {
    formData.append("conversation_id", conversationId);
  }
  if (Platform.OS === "web") {
    // Web: fetch the audio blob
    const audioBlob = await fetch(audioUri).then((r) => r.blob());
    const extension = getAudioExtensionFromMimeType(audioBlob.type);
    formData.append("audio_file", audioBlob, `recording.${extension}`);
  } else {
    // Native: use the file URI directly
    formData.append("audio_file", {
      uri: audioUri,
      type: "audio/m4a",
      name: "recording.m4a",
    } as any);
  }

  const response = await api.post(`/chat/transcribe`, formData, {
    headers: {
      "Content-Type": "multipart/form-data",
    },
    responseType: "json",
    adapter: "fetch",
  });
  return response.data?.text;
};

const postVoiceMessageStream = async (
  queryClient: QueryClient,
  setIsThinking: ConversationComposition["setIsThinking"],
  setInputControl: ConversationComposition["setInputControl"],
  conversationId: string,
  audioUri: string,
  agent?: Agent,
  useCase?: UseCase,
) => {
  // Create FormData for multipart upload
  const formData = new FormData();

  // Determine file type and create blob for web, or use file URI for native
  if (Platform.OS === "web") {
    // Web: fetch the audio blob
    const audioBlob = await fetch(audioUri).then((r) => r.blob());
    const extension = getAudioExtensionFromMimeType(audioBlob.type);
    formData.append("audio_file", audioBlob, `recording.${extension}`);
  } else {
    // Native: use the file URI directly
    formData.append("audio_file", {
      uri: audioUri,
      type: "audio/m4a",
      name: "recording.m4a",
    } as any);
  }

  // Add optional fields
  if (agent) {
    formData.append("initial_agent", agent);
  }
  if (useCase) {
    formData.append("contexts_theme", useCase);
  }

  const response = await api.post(`/chat/${conversationId}/voice`, formData, {
    headers: {
      "Content-Type": "multipart/form-data",
    },
    responseType: "stream",
    adapter: "fetch",
  });

  const reader = response.data.getReader();
  await parseEventStream(
    reader,
    queryClient,
    setIsThinking,
    setInputControl,
    conversationId,
  );
};

export const usePostNoriMessage = () => {
  const queryClient = useQueryClient();
  const { setIsThinking, setInputControl } = useConversationComposition();
  const { pushUserSentEvent } = useUserSentEventStore();
  const { clearConversationNudges } = useNudgeStore();

  return useMutation({
    mutationFn: ({
      message: textOfUserSentMessage,
      conversationId,
      agent,
      useCase,
      utmTerm,
      attachmentIds,
      type,
      heightWeight,
    }: {
      message: string | object[];
      conversationId: string;
      agent?: Agent;
      useCase?: UseCase;
      utmTerm?: string;
      attachmentIds?: string[];
      attachments?: AttachmentType[];
      type?: SendMessageRequestType;
      heightWeight?: HeightWeight;
    }) => {
      return postMessageStream(
        queryClient,
        setIsThinking,
        setInputControl,
        conversationId,
        textOfUserSentMessage,
        agent,
        useCase,
        utmTerm,
        attachmentIds,
        type,
        heightWeight,
      );
    },
    onError: (_, variables) => {
      setIsThinking(false, variables.conversationId);
      // TODO: handle error
      // queryClient.setQueryData(
      //   getGetConversationQueryKey(conversationId),
      //   context.previousTodos,
      // );
    },
    mutationKey: ["chat", "postMessage"],
    onSuccess: (
      _data,
      {
        conversationId,
      }: {
        conversationId: string;
        message: string | object[];
        agent?: Agent;
        useCase?: UseCase;
        utmTerm?: string;
        attachmentIds?: string[];
        attachments?: AttachmentType[];
        type?: SendMessageRequestType;
        heightWeight?: HeightWeight;
      },
    ) => {
      queryClient.invalidateQueries({
        queryKey: ["conversation", "infinite", conversationId],
        exact: false,
      });

      // Invalidate the conversations list to update the title
      // The backend sets the conversation title based on the first message
      invalidateConversationHistory(queryClient);
    },
    onMutate: async ({
      message: textOfUserSentMessage,
      conversationId,
      attachments,
      type,
    }: {
      message: string | object[];
      conversationId: string;
      agent?: Agent;
      useCase?: UseCase;
      utmTerm?: string;
      attachmentIds?: string[];
      attachments?: AttachmentType[];
      type?: SendMessageRequestType;
      heightWeight?: HeightWeight;
    }) => {
      setTag("event.sent_message.nori", true);
      clearConversationNudges();
      setIsThinking(true, conversationId);

      const queryKey = infiniteMessagesQueryKey(conversationId);
      await queryClient.cancelQueries({ queryKey });

      if (type === SendMessageRequestType.ask_user_questions_response) {
        queryClient.setQueryData(
          queryKey,
          updateInfiniteMessagesFromQuestionAnswers(
            conversationId,
            textOfUserSentMessage as AskUserQuestionAnswer[],
            attachments,
          ),
        );
      } else if (
        // Skip optimistic updates for rx_input/location_input - these are handled by SSE events
        type !== SendMessageRequestType.rx_input &&
        type !== SendMessageRequestType.rx_renewal_selected_medication &&
        type !== SendMessageRequestType.location_input &&
        type !== SendMessageRequestType.talk_to_doctor_ai_upsell_accept
      ) {
        const messageText = textOfUserSentMessage as string;
        const userSentEvent = createUserSentMessage(
          conversationId,
          messageText,
        );
        pushUserSentEvent(userSentEvent);

        queryClient.setQueryData(
          queryKey,
          updateInfiniteMessagesFromUser(
            conversationId,
            messageText,
            attachments,
          ),
        );
      }
    },
  });
};

const usePostVoiceMessage = () => {
  const queryClient = useQueryClient();
  const { setIsThinking, setIsTranscribing, setInputControl } =
    useConversationComposition();
  const { pushUserSentEvent } = useUserSentEventStore();
  const { clearConversationNudges } = useNudgeStore();

  return useMutation({
    mutationFn: ({
      audioUri,
      conversationId,
      agent,
      useCase,
    }: {
      audioUri: string;
      conversationId: string;
      agent?: Agent;
      useCase?: UseCase;
    }) =>
      postVoiceMessageStream(
        queryClient,
        setIsThinking,
        setInputControl,
        conversationId,
        audioUri,
        agent,
        useCase,
      ),
    onError: (_, variables) => {
      setIsThinking(false, variables.conversationId);
      // TODO: handle error - could show toast notification
    },
    mutationKey: ["chat", "postVoiceMessage"],
    onSuccess: (
      _data,
      {
        conversationId,
      }: {
        conversationId: string;
        audioUri: string;
        agent?: Agent;
        useCase?: UseCase;
      },
    ) => {
      queryClient.invalidateQueries({
        queryKey: ["conversation", "infinite", conversationId],
        exact: false,
      });

      // Invalidate the conversations list to update the title
      invalidateConversationHistory(queryClient);
    },
    onMutate: async ({
      conversationId,
    }: {
      audioUri: string;
      conversationId: string;
      agent?: Agent;
      useCase?: UseCase;
    }) => {
      setTag("event.sent_message.nori", true);
      clearConversationNudges();
      setIsTranscribing(false);

      const queryKey = infiniteMessagesQueryKey(conversationId);
      await queryClient.cancelQueries({ queryKey });

      //TODO is it ok to write an event here?
      const textOfUserSentMessage = "[Voice message]";
      const userSentEvent = createUserSentMessage(
        conversationId,
        textOfUserSentMessage,
      );
      pushUserSentEvent(userSentEvent);

      // Add placeholder message for voice recording
      queryClient.setQueryData(
        queryKey,
        updateInfiniteMessagesFromUser(conversationId, textOfUserSentMessage),
      );
    },
  });
};

const usePostVoiceTranscription = () => {
  const { setText, setIsTranscribing } = useConversationComposition();

  return useMutation({
    mutationFn: ({
      audioUri,
      conversationId,
    }: {
      audioUri: string;
      conversationId?: string;
    }) => postVoiceMessageTranscription(audioUri, conversationId),
    onError: (_, variables) => {
      // TODO: handle error - could show toast notification
      setIsTranscribing(false);
    },
    mutationKey: ["chat", "postVoiceTranscription"],
    onSuccess: (
      _data,
      {
        conversationId,
      }: {
        audioUri: string;
        conversationId?: string;
      },
    ) => {
      // populate chat box with transcription
      const transcribedText = _data;
      setIsTranscribing(false);
      setText(transcribedText, conversationId);
    },
    onMutate: async ({
      conversationId,
    }: {
      audioUri: string;
      conversationId?: string;
    }) => {
      // TODO: Set animation for transcribing in chat box?
      setIsTranscribing(true);
    },
  });
};

export const useSendVoiceTranscription = () => {
  const conversationId = useCurrentConversationId();

  const { mutate: createConversation } = useCreateConversation();
  const router = usePropagatedRouter();
  const { track } = useAnalytics();
  const eventContext = useEventContext();
  const queryClient = useQueryClient();
  const { getLocalPartnerIntent } = usePartnerIntent();

  const { mutate: postVoiceTranscription } = usePostVoiceTranscription();
  const { shouldStartNewConversation, conversationGroupId } =
    useJoinGroupOnSend(conversationId);

  const sendVoiceTranscription = (audioUri: string) => {
    // Create a conversation when there isn't one or join the existing group
    // when the group's newest conversation has closed.
    // TODO: Should we be doing this for transcription?
    if (!conversationId || shouldStartNewConversation) {
      getLocalPartnerIntent().then((partnerIntent) => {
        createConversation(
          {
            data: {
              partner_intent: partnerIntent ?? null,
              conversation_group_id: conversationGroupId,
            },
          },
          {
            onSuccess: (data) => {
              const conversationId = data?.conversation_id;

              // Invalidate immediately so the conversation appears in the sidebar
              invalidateConversationHistory(queryClient);

              // TODO: remove after generalist refactor rollout is 100%
              queryClient.invalidateQueries({
                queryKey: getGetUserProfileQueryKey(),
              });

              track(AD_CONVERSION_EVENTS.conversationCreated, {
                event_context: {
                  ...eventContext,
                  conversation_id: conversationId,
                  conversation_type: "nori",
                },
              });
              router.navigate(`/conversation/${conversationId}`);
              postVoiceTranscription({ audioUri, conversationId });
            },
          },
        );
      });
    } else {
      postVoiceTranscription({ audioUri, conversationId });
    }
  };
  return { sendVoiceTranscription };
};

export const useSendVoiceMessage = () => {
  const { id: conversationId, useCase: useCaseParam } = useLocalSearchParams<{
    id?: string;
    useCase?: UseCase;
  }>();
  const { clearInputControl } = useConversationComposition();
  const { mutate: createConversation } = useCreateConversation();
  const { mutate: postVoiceMessage } = usePostVoiceMessage();
  const router = usePropagatedRouter();
  const { track } = useAnalytics();
  const eventContext = useEventContext();
  const { data: messages } = useInfiniteMessages(conversationId);
  const queryClient = useQueryClient();
  const { chatScrollViewRef } = useContext(LayoutContext);
  const { setConversationUseCase } = useUseCaseStore();
  const { getLocalPartnerIntent } = usePartnerIntent();
  const { shouldStartNewConversation, conversationGroupId } =
    useJoinGroupOnSend(conversationId);

  const sendVoiceMessage = (
    audioUri: string,
    agent?: Agent,
    useCase?: UseCase,
  ) => {
    // Create a conversation when there isn't one or join the existing group
    // when the group's newest conversation has closed.
    if (!conversationId || shouldStartNewConversation) {
      getLocalPartnerIntent().then((partnerIntent) => {
        createConversation(
          {
            data: {
              partner_intent: partnerIntent ?? null,
              conversation_group_id: conversationGroupId,
            },
          },
          {
            onSuccess: (data) => {
              const conversationId = data?.conversation_id;

              // Invalidate immediately so the conversation appears in the sidebar
              invalidateConversationHistory(queryClient);

              // TODO: remove after generalist refactor rollout is 100%
              queryClient.invalidateQueries({
                queryKey: getGetUserProfileQueryKey(),
              });

              track(AD_CONVERSION_EVENTS.conversationCreated, {
                event_context: {
                  ...eventContext,
                  conversation_id: conversationId,
                  conversation_type: "nori",
                },
              });
              const finalUseCase =
                useCase ?? (useCaseParam as UseCase | undefined);
              if (finalUseCase) {
                setConversationUseCase(conversationId, finalUseCase);
              }
              router.navigate(`/conversation/${conversationId}`);
              postVoiceMessage({
                audioUri,
                conversationId,
                agent,
                useCase: finalUseCase,
              });
            },
          },
        );
      });
    } else {
      // Otherwise, just send the voice message
      const countUserMessagesInCurrentConvo = (
        messages?.allMessages || []
      ).filter((message) => message.role === "user").length;

      if (countUserMessagesInCurrentConvo === 4) {
        track(AD_CONVERSION_EVENTS.fifthMessageSent, {
          event_context: eventContext,
        });
      }

      clearInputControl();
      postVoiceMessage({ audioUri, conversationId });
      requestAnimationFrame(() => {
        // Wait for Flashlist to update layout
        requestAnimationFrame(() => {
          // Finally, scroll to the end of the chat view
          chatScrollViewRef.current?.scrollToEnd({ animated: true });
        });
      });
    }
  };

  return { sendVoiceMessage };
};

export interface SendNoriMessageOptions {
  agent?: Agent;
  useCase?: UseCase;
  workflowType?: WorkflowType;
  attachmentIds?: string[];
  attachments?: AttachmentType[];
  type?: SendMessageRequestType;
  heightWeight?: HeightWeight;
  onConversationCreation?: (conversationId: string) => void;
  onSuccess?: () => void;
}

export const useSendNoriMessage = (conversationIdOverride?: string) => {
  const {
    id: conversationIdFromParams,
    useCase: useCaseParam,
    utm_term,
  } = useLocalSearchParams<{
    id?: string;
    useCase?: UseCase;
    utm_term?: string;
  }>();
  const conversationId = conversationIdOverride ?? conversationIdFromParams;
  const { clearInputControl, setText } = useConversationComposition();
  const {
    mutate: createConversation,
    isPending: isConversationCreationPending,
  } = useCreateConversation();
  const { mutate: postMessage, isPending: isMessagePending } =
    usePostNoriMessage();
  const { chatScrollViewRef } = useContext(LayoutContext);
  const router = usePropagatedRouter();
  const { track } = useAnalytics();
  const eventContext = useEventContext();
  const { setConversationUseCase } = useUseCaseStore();
  const { data: messages } = useInfiniteMessages(conversationId);
  const queryClient = useQueryClient();
  const { getLocalPartnerIntent } = usePartnerIntent();
  const { shouldStartNewConversation, conversationGroupId } =
    useJoinGroupOnSend(conversationId);

  const sendNoriMessage = (
    freetextMessage: string | object[],
    {
      agent,
      useCase,
      workflowType,
      attachmentIds,
      attachments,
      type,
      heightWeight,
      onConversationCreation,
      onSuccess,
    }: SendNoriMessageOptions = {},
  ) => {
    // If there's an active AskUserQuestions session and user submits freetext, we need to include already answered Qs from the store
    const sessionResult = getStructuredPartialAnswersAndClear(freetextMessage);
    const message = sessionResult?.answers ?? freetextMessage;
    const effectiveType = sessionResult
      ? SendMessageRequestType.ask_user_questions_response
      : type;
    const mergedAttachmentIds = [
      ...(sessionResult?.attachmentIds ?? []),
      ...(attachmentIds ?? []),
    ];
    const mergedAttachments = [
      ...(sessionResult?.attachments ?? []),
      ...(attachments ?? []),
    ];
    const isEmptyString = typeof message === "string" && !message.trim();
    if (
      isEmptyString &&
      !mergedAttachmentIds.length &&
      type !== SendMessageRequestType.talk_to_doctor &&
      type !== SendMessageRequestType.talk_to_doctor_ai_upsell_accept
    )
      return;
    // Create a conversation when there isn't one or join the existing group
    // when the group's newest conversation has closed.
    if (!conversationId || shouldStartNewConversation) {
      getLocalPartnerIntent().then((partnerIntent) => {
        createConversation(
          {
            data: {
              initial_workflow_type: workflowType ?? null,
              partner_intent: partnerIntent ?? null,
              conversation_group_id: conversationGroupId,
            },
          },
          {
            onSuccess: (data) => {
              const conversationId = data?.conversation_id;

              // Invalidate immediately so the conversation appears in the sidebar
              invalidateConversationHistory(queryClient);

              // TODO: remove after generalist refactor rollout is 100%
              queryClient.invalidateQueries({
                queryKey: getGetUserProfileQueryKey(),
              });

              track(AD_CONVERSION_EVENTS.conversationCreated, {
                event_context: {
                  ...eventContext,
                  conversation_id: conversationId,
                  conversation_type: "nori",
                },
              });
              // reset text input for new conversation
              setText("");
              const finalUseCase =
                useCase ?? (useCaseParam as UseCase | undefined);
              if (finalUseCase) {
                setConversationUseCase(conversationId, finalUseCase);
              }
              // Pre-seed the conversation query cache so the optimistic update
              // in postMessage preserves initial_workflow_type (prevents button flash).
              // Seeding conversation_group_id lets the stream resolve the group from the
              // cache immediately instead of flashing an empty "new chat"
              queryClient.setQueryData(
                infiniteMessagesQueryKey(conversationId),
                {
                  pages: [
                    {
                      conversation: {
                        id: conversationId,
                        initial_workflow_type: workflowType ?? null,
                        conversation_group_id: conversationGroupId,
                      } as ConversationModel,
                      messages: [],
                      pagination: {
                        limit: 25,
                        has_more: false,
                        next_cursor: null,
                      },
                    },
                  ],
                  pageParams: [null],
                },
              );
              router.navigate(`/conversation/${conversationId}`);
              onConversationCreation?.(conversationId);
              postMessage(
                {
                  message,
                  conversationId,
                  agent,
                  useCase: finalUseCase,
                  utmTerm: (utm_term as string | undefined) ?? undefined,
                  attachmentIds: mergedAttachmentIds,
                  attachments: mergedAttachments,
                  type: effectiveType,
                  heightWeight,
                },
                { onSuccess },
              );
            },
          },
        );
      });
    } else {
      // Otherwise, just send the message
      const countUserMessagesInCurrentConvo = (
        messages?.allMessages || []
      ).filter((message) => message.role === "user").length;

      if (countUserMessagesInCurrentConvo === 4) {
        track(AD_CONVERSION_EVENTS.fifthMessageSent, {
          event_context: eventContext,
        });
      }

      clearInputControl();
      setText("");
      postMessage(
        {
          message,
          conversationId,
          agent,
          useCase: useCase ?? (useCaseParam as UseCase | undefined),
          attachmentIds: mergedAttachmentIds,
          attachments: mergedAttachments,
          type: effectiveType,
          heightWeight,
        },
        { onSuccess },
      );
      // Wait for TanStack to update query data and scroll to end
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          chatScrollViewRef.current?.scrollToEnd({ animated: true });
        });
      });
    }
  };

  return {
    sendNoriMessage,
    isPending: isConversationCreationPending || isMessagePending,
  };
};

export const useSendRxInput = (conversationIdOverride?: string) => {
  const { sendNoriMessage } = useSendNoriMessage(conversationIdOverride);

  const sendRxInput = useCallback(
    (message: string, heightWeight?: HeightWeight) => {
      sendNoriMessage(message, {
        type: SendMessageRequestType.rx_input,
        heightWeight,
      });
    },
    [sendNoriMessage],
  );

  return { sendRxInput };
};

export const useSendLocationInput = (conversationIdOverride?: string) => {
  const { sendNoriMessage } = useSendNoriMessage(conversationIdOverride);

  const sendLocationInput = useCallback(
    (state: string) => {
      sendNoriMessage(state, { type: SendMessageRequestType.location_input });
    },
    [sendNoriMessage],
  );

  return { sendLocationInput };
};

export type SendCuraiMessageVariables = {
  data: SendCuraiMessageRequest;
  conversationId: string;
  attachments?: AttachmentType[];
};

export const useSendCuraiMessage = () => {
  const queryClient = useQueryClient();
  const conversationId = useCurrentConversationId();
  const { pushUserSentEvent } = useUserSentEventStore();

  return useSendMessageInCuraiVisit({
    mutation: {
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: ["conversation", "infinite", conversationId],
          exact: false,
        });
      },
      onMutate: async (variables: SendCuraiMessageVariables) => {
        const {
          data: { message: textOfUserSentMessage },
          conversationId,
          attachments,
        } = variables;
        setTag("event.sent_message.curai", true);

        const queryKey = infiniteMessagesQueryKey(conversationId);
        await queryClient.cancelQueries({ queryKey });

        if (textOfUserSentMessage) {
          const userSentMessage = createUserSentMessage(
            conversationId,
            textOfUserSentMessage,
          );
          pushUserSentEvent(userSentMessage);
        }

        queryClient.setQueryData(
          queryKey,
          updateInfiniteMessagesFromUser(
            conversationId,
            textOfUserSentMessage ?? "",
            attachments,
          ),
        );
      },
    },
  });
};

export const useIsChatMutationPending = () => {
  const conversationId = useCurrentConversationId();
  const count = useIsMutating({
    mutationKey: ["chat", "postMessage"],
    predicate: (mutation) => {
      const mutationVars = mutation.state.variables as {
        conversationId?: string;
      };
      return mutationVars?.conversationId === conversationId;
    },
  });
  return Boolean(count);
};

export const useUpdateEvent = () => {
  const conversationId = useCurrentConversationId();
  const queryClient = useQueryClient();
  const { mutate, ...rest } = useGeneratedUpdateEvent({
    mutation: {
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: infiniteMessagesQueryKey(conversationId!),
        });
      },
    },
  });
  return { mutate, ...rest };
};

export const useMarkConversationAsRead = (
  conversationGroupId?: string | null,
) => {
  const queryClient = useQueryClient();
  return useGeneratedMarkConversationAsRead({
    mutation: {
      onMutate: ({ conversationId }) => {
        setConversationRead(conversationId, queryClient);
        if (conversationGroupId) {
          setConversationGroupRead(conversationGroupId, queryClient);
        }
      },
    },
  });
};

export const useSaveNotificationToConversation = () => {
  const conversationId = useCurrentConversationId();
  const queryClient = useQueryClient();
  const router = useRouter();
  return useGeneratedSaveNotificationToConversation({
    mutation: {
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: ["conversation", "infinite", conversationId],
          exact: false,
        });
        router.setParams({ notificationId: undefined });
      },
    },
  });
};
